import { useCallback, useEffect, useRef, useState } from "react";
import type { BgView } from "@bing/engine-backgammon";
import { boardFromView } from "@bing/engine-backgammon";
import {
  advance,
  compressedHold,
  diffToEvents,
  eventTiming,
  remainingAfter,
  unapply,
  type BgEvent,
} from "./backgammon-replay";
import { prefersReducedMotion } from "./card-motion";

/* ------------------------------------------------------------------ *
 * The REPLAY layer's React half: a queue and a scheduler.
 *
 * `BackgammonTable` renders THIS view rather than the authoritative one,
 * so an opponent's checkers can never teleport however fast their frames
 * arrive. The queue paces the gap BETWEEN events and enforces a MINIMUM
 * interval — an already-paced bot (the engine holds 2600ms after a roll
 * and 950ms between sub-moves) passes straight through and is never
 * double-delayed.
 *
 * MY OWN sub-moves bypass the pacing (hold 0): I tapped, so the result
 * must be immediate. Their motion still plays, and the table supports
 * several flights at once, so fast tapping shows several checkers moving
 * rather than one cancelling the next.
 * ------------------------------------------------------------------ */

export interface BgReplay {
  /** The view to RENDER — behind the authoritative one while draining. */
  view: BgView;
  /** The event currently playing; drives the table's motion. */
  event: BgEvent | null;
  /** True while the rendered view is behind the authoritative one. */
  draining: boolean;
  /** Fast-forward to the authoritative position (a tap during a drain). */
  catchUp: () => void;
}

interface Pending {
  event: BgEvent;
  /** The authoritative view this event's batch is heading toward. */
  target: BgView;
}

/** Apply ONE event to a rendered view, returning the next rendered view. */
function applyEvent(from: BgView, e: BgEvent, target: BgView): BgView {
  if (e.kind === "roll") {
    return { ...from, dice: e.dice, diceRemaining: e.dice, isDoubles: e.isDoubles };
  }
  if (e.kind === "walk" || e.kind === "undo") {
    const board = boardFromView(from);
    const mover: 0 | 1 = e.mover === 0 ? 0 : 1;
    if (e.kind === "walk") advance(board, mover, e.sub.from, e.sub.to);
    else unapply(board, mover, e.sub.from, e.sub.to, e.unhit);
    const staged =
      e.kind === "walk"
        ? [...from.staged, e.sub]
        : from.staged.slice(0, Math.max(0, from.staged.length - 1));
    return {
      ...from,
      points: board.points,
      bar: [board.bar[0], board.bar[1]],
      off: [board.off[0], board.off[1]],
      staged,
      // The spent dice follow what has actually been SHOWN, so the ×N badge and the dimmed dice
      // never run ahead of the checkers — and each one is removed by the die it CONSUMED, not by
      // position (see `remainingAfter`: `slice(n)` dimmed the wrong die on any non-double played
      // out of order).
      diceRemaining: remainingAfter(from.dice, mover, staged),
    };
  }
  // A handover settles on the authoritative truth (turn, legality, hints all belong to it now).
  if (e.kind === "handover") return target;
  if (e.kind === "dance") return { ...from, noLegalMove: true };
  return from; // openingRollOff moves nothing
}

export function useBgReplay(authoritative: BgView): BgReplay {
  const [rendered, setRendered] = useState<BgView | null>(null);
  const [event, setEvent] = useState<BgEvent | null>(null);
  // `draining` is STATE, not a ref read during render: a ref would be stale for the very render
  // that must already suppress the highlights.
  const [draining, setDraining] = useState(false);
  const queue = useRef<Pending[]>([]);
  const timer = useRef<number | null>(null);
  /** SEPARATE from `timer`: `timer !== null` is the "a drain is in flight" guard that stops a new
   *  batch double-starting, so the trailing event's tidy-up must NOT occupy it — doing so left an
   *  arriving batch permanently unstarted. */
  const clearEventTimer = useRef<number | null>(null);
  const renderedRef = useRef<BgView | null>(null);
  renderedRef.current = rendered;

  const clearTimer = useCallback((): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
    if (clearEventTimer.current !== null) {
      window.clearTimeout(clearEventTimer.current);
      clearEventTimer.current = null;
    }
  }, []);
  // Leaving the table must never let a queued motion land in the lobby.
  useEffect(() => clearTimer, [clearTimer]);

  const step = useCallback((): void => {
    const next = queue.current.shift();
    if (!next) {
      setEvent(null);
      setDraining(false);
      return;
    }
    setEvent(next.event);
    setRendered((cur) => applyEvent(cur ?? next.target, next.event, next.target));
    if (queue.current.length === 0) {
      // Settle on the authoritative view so nothing derived stays stale.
      setRendered(next.target);
      setDraining(false);
      // The LAST event still owns its motion, but it MUST then be cleared: leaving it set left the
      // one-shot opening ceremony on screen for the rest of the game (caught in a real browser —
      // the overlay was still up 3.4s in). Clear it once its motion has had its time.
      const done = eventTiming(next.event);
      if (done.motionMs > 0) {
        clearEventTimer.current = window.setTimeout(() => {
          clearEventTimer.current = null;
          setEvent(null);
        }, done.motionMs);
      } else {
        setEvent(null);
      }
      return;
    }
    setDraining(true);
    const t = eventTiming(next.event);
    // My own sub-moves are not paced — I tapped, so the next one must not wait.
    const mine =
      (next.event.kind === "walk" || next.event.kind === "undo") &&
      next.event.mover === next.target.you;
    const hold = mine ? 0 : compressedHold(t.holdMs, queue.current.length);
    if (hold <= 0) {
      step();
      return;
    }
    timer.current = window.setTimeout(() => {
      timer.current = null;
      step();
    }, hold);
  }, []);

  const catchUp = useCallback((): void => {
    clearTimer();
    queue.current = [];
    setEvent(null);
    setDraining(false);
    setRendered(authoritative);
  }, [authoritative, clearTimer]);

  useEffect(() => {
    /*
     * ⚠️ A FRAME THAT LANDS MID-DRAIN IS DIFFED AGAINST WHERE THE QUEUE IS ALREADY HEADING, not
     * against the board on screen (card-98 6.4). The screen is BEHIND while draining, so diffing it
     * re-queued moves already queued, and a whole turn cycle inside one drain — the opponent's
     * moves, my turn no roll can play, the server's pass 2 s later — read as the opponent taking
     * their moves back: checkers walked backward before the board snapped right.
     */
    const heading = queue.current[queue.current.length - 1];
    const events = diffToEvents(heading?.target ?? renderedRef.current, authoritative);
    if (!prefersReducedMotion() && events.length === 0 && heading) {
      // Nothing new to show (a re-sent frame): keep pacing, and settle on the newest view.
      heading.target = authoritative;
      return;
    }
    if (prefersReducedMotion() || events.length === 0) {
      clearTimer();
      queue.current = [];
      setEvent(null);
      setDraining(false);
      setRendered(authoritative);
      return;
    }
    queue.current.push(...events.map((e) => ({ event: e, target: authoritative })));
    if (timer.current === null) step();
  }, [authoritative, clearTimer, step]);

  const base = rendered ?? authoritative;
  // While draining, the board is deliberately BEHIND the authoritative position, so nothing on it
  // may look tappable: a tap would act on a position the player is not being shown.
  const view: BgView = draining
    ? { ...base, legalFrom: [], legalTo: {}, mustConfirm: false }
    : base;
  return { view, event, draining, catchUp };
}
